/**
 * HelpSupportPage.jsx — Help & Support hub (Web)
 *
 * Sections:
 *   1. Ask AI — sends a help-context-enriched prompt to the AI assistant
 *   2. FAQs — collapsible accordion
 *   3. Contact Support — email the Greybeard support team
 *   4. App Info
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { sendAiMessage } from '../../api/aiChat.api';
import {
  HelpCircle, Sparkles, Send, ChevronDown, ChevronUp,
  Mail, User, MessageCircle, Info, Loader2, LifeBuoy,
} from 'lucide-react';
import { PageFadeSection } from '../../components/common/PageFadeSection';

/* ─── Support contacts ─── */
const CONTACTS = [
  { name: 'Naseer Ahmed',    email: 'naseer.ahmed@greybeardsupport.com' },
  { name: 'Umair Khan',      email: 'umair.khan@greybeardsupport.com' },
  { name: 'Muhammad Faizan', email: 'muhammad.faizan@greybeardsupport.com' },
];

/* ─── AI system context ─── */
const AI_HELP_CONTEXT =
  'You are an in-app help assistant for Operis — a workforce time-management platform integrated with SAP. ' +
  'Users can clock in/out, create time entries, scan production lot barcodes, view KPI dashboards, run AI analytics, ' +
  'manage teams, sync data to SAP, generate reports, and more. The app has a web (React) client and a mobile (React Native / Expo) client, ' +
  'both backed by a .NET API with Azure AI services. ' +
  'Answer the user\'s support question clearly and concisely. If you don\'t know, suggest they contact support.';

/* ══════════════════════════════════════════ */
const HelpSupportPage = () => {
  const { darkMode } = useTheme();
  const { t } = useTranslation();

  const faqs = useMemo(
    () =>
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => ({
        q: t(`pages.help.faq${i}q`),
        a: t(`pages.help.faq${i}a`),
      })),
    [t]
  );

  /* ── FAQ accordion ── */
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = useCallback((i) => setOpenFaq((prev) => (prev === i ? null : i)), []);

  /* ── Ask AI ── */
  const [question, setQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const askAi = useCallback(async () => {
    if (!question.trim()) return;
    setAiLoading(true);
    setAiAnswer('');
    try {
      const prompt = `[HELP CONTEXT] ${AI_HELP_CONTEXT}\n\n[USER QUESTION] ${question.trim()}`;
      const res = await sendAiMessage({ message: prompt });
      const d = res?.data?.data || res?.data;
      setAiAnswer(d?.response || d?.message || t('pages.help.aiNoAnswer'));
    } catch {
      setAiAnswer(t('pages.help.aiError'));
    } finally {
      setAiLoading(false);
    }
  }, [question, t]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAi(); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">

      <PageFadeSection>
      {/* ── Hero ── */}
      <div className="text-center space-y-3">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${
          darkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'
        }`}>
          <LifeBuoy className="w-8 h-8 text-indigo-500" />
        </div>
        <h1 className={`text-2xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('pages.help.heroTitle')}
        </h1>
        <p className={`text-sm max-w-md mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('pages.help.heroSub')}
        </p>
      </div>

      </PageFadeSection>
      <PageFadeSection>
      {/* ── Ask AI ── */}
      <Section title={t('pages.help.askAiTitle')} icon={Sparkles} darkMode={darkMode}>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('pages.help.askAiDesc')}
        </p>
        <div className="flex gap-3">
          <textarea
            className={`flex-1 min-h-[44px] max-h-32 resize-none rounded-xl px-4 py-3 text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ${
              darkMode
                ? 'bg-white/[0.04] border-white/10 text-white placeholder-gray-500'
                : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
            }`}
            placeholder={t('pages.help.askPlaceholder')}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={500}
          />
          <button
            className="self-end flex items-center justify-center w-11 h-11 rounded-xl bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-40 transition-colors"
            onClick={askAi}
            disabled={aiLoading || !question.trim()}
          >
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        {aiAnswer && (
          <div className={`mt-4 flex gap-3 rounded-xl p-4 text-sm leading-relaxed border ${
            darkMode
              ? 'bg-white/[0.03] border-white/[0.06] text-gray-200'
              : 'bg-gray-50 border-gray-100 text-gray-700'
          }`}>
            <MessageCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-indigo-500" />
            <p className="whitespace-pre-wrap">{aiAnswer}</p>
          </div>
        )}
      </Section>
      </PageFadeSection>
      <PageFadeSection>
      {/* ── FAQ ── */}
      <Section title={t('pages.help.faqTitle')} icon={HelpCircle} darkMode={darkMode}>
        <div className="space-y-2">
          {faqs.map((faq, i) => {
            const open = openFaq === i;
            return (
              <button
                key={i}
                onClick={() => toggleFaq(i)}
                className={`w-full text-left rounded-xl px-4 py-3.5 border transition-all ${
                  open
                    ? darkMode
                      ? 'bg-white/[0.04] border-indigo-500/30'
                      : 'bg-indigo-50/40 border-indigo-200'
                    : darkMode
                      ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'
                      : 'bg-white border-gray-100 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {faq.q}
                  </span>
                  {open
                    ? <ChevronUp className="w-4 h-4 flex-shrink-0 text-indigo-500" />
                    : <ChevronDown className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  }
                </div>
                {open && (
                  <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {faq.a}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </Section>
      </PageFadeSection>

      <PageFadeSection>
      {/* ── Contact Support ── */}
      <Section title={t('pages.help.contactTitle')} icon={Mail} darkMode={darkMode}>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('pages.help.contactSub')}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          {CONTACTS.map((c, i) => (
            <a
              key={i}
              href={`mailto:${c.email}?subject=Operis%20Support%20Request`}
              className={`flex items-center gap-3 rounded-xl p-4 border transition-all hover:-translate-y-0.5 hover:shadow-md ${
                darkMode
                  ? 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'
                  : 'bg-white border-gray-100 hover:bg-indigo-50/30'
              }`}
            >
              <div className={`flex items-center justify-center w-10 h-10 rounded-xl ${
                darkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'
              }`}>
                <User className="w-4 h-4 text-indigo-500" />
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {c.name}
                </p>
                <p className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {c.email}
                </p>
              </div>
            </a>
          ))}
        </div>
      </Section>
      </PageFadeSection>

      <PageFadeSection>
      {/* ── App Info ── */}
      <Section title={t('pages.help.aboutTitle')} icon={Info} darkMode={darkMode}>
        <div className={`rounded-xl p-5 border divide-y ${
          darkMode ? 'bg-white/[0.02] border-white/[0.06] divide-white/[0.06]' : 'bg-white border-gray-100 divide-gray-100'
        }`}>
          {[
            [t('pages.help.rowApp'), t('pages.help.rowAppVal')],
            [t('pages.help.rowPlatform'), t('pages.help.rowPlatformVal')],
            [t('pages.help.rowBackend'), t('pages.help.rowBackendVal')],
            [t('pages.help.rowSap'), t('pages.help.rowSapVal')],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between py-3 first:pt-0 last:pb-0">
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
              <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{value}</span>
            </div>
          ))}
        </div>
      </Section>
      </PageFadeSection>

      <div className="h-8" />
    </div>
  );
};

/* ── Reusable section wrapper ── */
const Section = ({ title, icon: Icon, darkMode, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-indigo-500" />
      <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
    </div>
    {children}
  </div>
);

export default HelpSupportPage;
